import React from 'react';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ChartOptions,
	TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { IIncident } from 'types/Branches';
import {
	IGraphCriticalAction,
	IGraphCriticalActionStatus,
} from 'types/VehicleIncidents';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend
);

export interface IIncidentCriticalActionsAtAGlanceGraph {
	graphCriticalActions: IGraphCriticalAction[] | undefined;
}

const IncidentCriticalActionsAtAGlanceGraph: React.FC<
	IIncidentCriticalActionsAtAGlanceGraph
> = ({ graphCriticalActions }) => {
	const options: ChartOptions<'bar'> = {
		plugins: {
			legend: {
				align: 'start',
				position: 'bottom',
				fullSize: true,
				labels: {
					usePointStyle: true,
					pointStyle: 'rect',
					boxHeight: 8,
					font: {
						size: 12,
					},
				},
			},
			tooltip: {
				displayColors: true,
				callbacks: {
					//removes default tooltip body information
					label() {
						return '';
					},
					afterBody(context: TooltipItem<'bar'>[]) {
						const currentCriticalAction = graphCriticalActions?.find(
							(currentMonthData: IGraphCriticalAction) =>
								currentMonthData.name === context[0].label
						);

						if (currentCriticalAction) {
							const resultString = [];

							if (currentCriticalAction.total > 0) {
								resultString.push(`Incidents: ${currentCriticalAction.total}`);
							}

							//format and sort incidents to push to string
							const incidents = currentCriticalAction.statuses
								.map((status: IGraphCriticalActionStatus) => {
									return {
										name: status.name,
										incidents: status.total,
									};
								})
								.filter((status: IIncident) => status.incidents !== 0)
								.sort(
									(a: IIncident, b: IIncident) => b.incidents - a.incidents
								);

							incidents.forEach((incident: IIncident) =>
								resultString.push(` - ${incident.name}: ${incident.incidents}`)
							);

							if (currentCriticalAction.open > 0) {
								if (currentCriticalAction.total > 0) {
									resultString.push('');
								}

								resultString.push(`Open: ${currentCriticalAction.open}`);
							}

							return resultString;
						} else {
							return [`${context[0].dataset.label}: ${context[0].raw}`];
						}
					},
				},
				padding: {
					left: 10,
					right: 30,
					y: 10,
				},
				borderColor: 'rgba(52, 64, 84, 0.5)',
				borderWidth: 1,
				backgroundColor: '#FFFFFF',
				titleColor: '#344054',
				bodyColor: '#344054',
				bodyFont: {
					size: 11,
				},
			},
		},
		responsive: true,
		interaction: {
			mode: 'index' as const,
			intersect: false,
		},
		scales: {
			x: {
				stacked: true,
			},
			y: {
				stacked: true,
			},
		},
		maintainAspectRatio: false,
		// @ts-ignore - ChartJS types are simply incorrect
		barThickness: 24,
	};

	const data = {
		labels: graphCriticalActions?.map(
			(criticalAction: IGraphCriticalAction) => criticalAction.name
		),
		datasets: [
			{
				label: 'Open incidents total',
				data: graphCriticalActions?.map(
					(criticalAction: IGraphCriticalAction) => criticalAction.total
				),
				backgroundColor: '#FAA431',
				stack: 'Stack 0',
			},
		],
	};

	return <Bar options={options} data={data} redraw={true} />;
};

export default IncidentCriticalActionsAtAGlanceGraph;
